import { CartRecommendationsBase } from '@theme/cart-recommendations-base';
import { SubscriptionOpenEvent } from '@theme/events';

/**
 * Cart drawer recommended products carousel.
 *
 * The cards themselves are server-rendered by the
 * `cart-drawer-recommendations` snippet from the cross-sells configured on the
 * cart products (`customMA.crossSellAssociations`), so the whole host is absent
 * from the markup when no cart product has cross-sells. It only drives the
 * prev/next arrows (via
 * {@link CartRecommendationsBase}) and each card's two CTAs — one-time Add vs.
 * Subscribe, which opens the global subscription drawer.
 */
class CartDrawerRecommendations extends CartRecommendationsBase {
  connectedExtra() {
    // Toggle each card's selling_plan input before its form submits (see below).
    this._onActionClick = (event) => this.#handleActionClick(event);
    this.addEventListener('click', this._onActionClick);
  }

  disconnectedExtra() {
    this.removeEventListener('click', this._onActionClick);
  }

  /**
   * The cart dispatches its update event BEFORE the drawer markup is morphed, so
   * the track still holds the previous cart's cards at this point. The fresh
   * cards (or the removal of this host altogether) come from the server — only
   * the arrows need a recount, once the morph has landed.
   */
  onCartUpdate() {
    requestAnimationFrame(() => this.updateArrows());
  }

  /**
   * Handles a card's two CTAs:
   *   - "Add" (form submit) — one-time purchase; just shows the loader on the
   *     clicked button (product-form does the actual add).
   *   - "Subscribe" (type=button) — opens the global subscription drawer for the
   *     card's product + variant via SubscriptionOpenEvent; the drawer owns
   *     frequency selection and the add, so the card doesn't submit its form.
   * @param {MouseEvent} event
   */
  #handleActionClick(event) {
    const target = /** @type {HTMLElement} */ (event.target);

    // Subscribe → open the drawer (add mode). Decoupled from the add form.
    const subscribeButton = target.closest('[data-subscribe-drawer]');
    if (subscribeButton) {
      const card = subscribeButton.closest('.cart-drawer-rec-card');
      const handle = card?.getAttribute('data-product-handle');
      if (!handle) return;
      document.dispatchEvent(
        new SubscriptionOpenEvent({
          handle,
          variantId: card?.querySelector('input[name="id"]')?.value || '',
          mode: 'add',
        })
      );
      return;
    }

    // Add → one-time purchase. Only the clicked button shows the loader:
    // product-form flags every name="add" button aria-busy on submit, but the
    // loader CSS also requires data-loading, so the unclicked sibling stays as-is.
    const button = target.closest('button[type="submit"]');
    if (!button) return;

    const card = button.closest('.cart-drawer-rec-card');
    if (!card) return;

    card.querySelectorAll('button[data-loading]').forEach((other) => other.removeAttribute('data-loading'));
    button.setAttribute('data-loading', '');
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
